import React, { useMemo } from 'react';
import { LineChart } from '@mantine/charts';
import {RentContract} from "../../../models/RentContract";


interface RevenueLast6MonthsChartProps {
    contracts: RentContract[];
}

export const RevenueLast6MonthsChart = ({ contracts }: RevenueLast6MonthsChartProps) => {

    // 1) Build the data for the last 6 months
    const chartData = useMemo(() => {
        // a) Get an array of the last 6 months (including current)
        const monthsArray = getLastSixMonths();
        // This might produce something like:
        // [ { year:2023, month:10, label:'2023-10' }, ..., 5 more items ]

        // b) For each month, sum up monthlyPrice from any overlapping contract
        return monthsArray.map((mObj) => {
            const totalForThisMonth = contracts.reduce((sum, contract) => {
                const start = new Date(contract.startDate);
                const end   = new Date(contract.endDate);

                // If the contract is 'active' at any point in this month
                // we add monthlyPrice
                if (overlapsWithMonth(start, end, mObj.year, mObj.month)) {
                    return sum + contract.monthlyPrice;
                }
                return sum;
            }, 0);

            return {
                month: mObj.label,         // e.g. "Oct 2023"
                revenue: totalForThisMonth,
            };
        });
    }, [contracts]);

    // 2) Render the chart
    // dataKey="month" means the 'month' field is the x-axis
    // We have one series for 'revenue'
    return (
        <LineChart
            h={300}
            data={chartData}         // e.g. [ { month:'Oct 2023', revenue:1500 }, ... ]
            dataKey="month"
            yAxisLabel="Euro"
            series={[
                { name: 'revenue', color: 'green.6', label: 'Umsatz'},
            ]}
            curveType="linear"
        />
    );
}

/**
 * Returns the last 6 months (including current).
 * Example output: [
 *   { year: 2023, month: 10, label: "Oct 2023" },
 *   { year: 2023, month: 9,  label: "Sep 2023" },
 *   ...
 * ]
 *
 * Adjust if you want from oldest to newest, etc.
 */
function getLastSixMonths() {
    const result: { year: number; month: number; label: string }[] = [];
    const now = new Date();

    // Start from this month's first day
    const currentYear  = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based index

    for (let i = 0; i < 6; i++) {
        // monthOffset from currentMonth going back i steps
        const y = currentYear;
        const m = currentMonth - i; // can be negative => last year
        const dateObj = new Date(y, m, 1);

        const finalYear  = dateObj.getFullYear();
        const finalMonth = dateObj.getMonth() + 1; // 1-based for display

        // build a label, e.g. "Oct 2023"
        const label = dateObj.toLocaleString('default', { month: 'short', year: 'numeric' });

        result.push({
            year: finalYear,
            month: finalMonth, // 1-based
            label,
        });
    }

    // This array goes from "current month" -> 1 month ago -> 2 months ago, ...
    // If you prefer oldest -> newest, reverse it:
    return result.reverse();
}

/**
 * Checks if a contract with [start, end] is active in a given (year, month).
 * "Active" means there's any overlap with that month.
 */
function overlapsWithMonth(
    start: Date,
    end: Date,
    year: number,
    month: number
): boolean {
    // The start of this month is (year, month, 1)
    // The end of this month is (year, month, lastDay)
    // We'll create date range [monthStart, monthEnd].
    const monthStart = new Date(year, month - 1, 1);
    // Next month start
    const nextMonthStart = new Date(year, month, 1);
    // So this month's end is nextMonthStart - 1 day
    const monthEnd = new Date(nextMonthStart.getTime() - 1 * 24 * 60 * 60 * 1000);

    // Overlap if (start <= monthEnd) and (end >= monthStart)
    return start <= monthEnd && end >= monthStart;
}
